/**
 * CAR-IA chip family (v3) — typed, reusable badges driven by design-tokens.
 *   - TrustChip      verified | pending | failed
 *   - DecisionChip   good-deal | fair-deal | overpriced
 *   - StatusChip     listing lifecycle
 *   - LeadChip       hot | medium | cold
 *   - PremiumBadge   gold premium marker
 *   - TasadorChip / PriceDropChip  domain price chips
 */
import React from 'react';
import type { TrustState, DecisionVerdict, ListingStatus, LeadTier } from '../../design-tokens';
import { trustStateToken, decisionToken, statusToken, leadToken } from '../../design-tokens';
import { CheckIcon, ClockIcon, StarIcon, ArrowDownIcon, SparkleIcon } from './icons';
import './caria-components.css';

export interface TrustChipProps {
  state: TrustState;
  label?: string;
}
export const TrustChip: React.FC<TrustChipProps> = ({ state, label }) => {
  const cls =
    state === 'verified'
      ? 'cia-chip--trust-verified'
      : state === 'failed'
      ? 'cia-chip--trust-failed'
      : 'cia-chip--trust-pending';
  return (
    <span className={`cia-chip ${cls}`}>
      {state === 'verified' && <CheckIcon />}
      {label ?? trustStateToken[state].label}
    </span>
  );
};

export interface DecisionChipProps {
  verdict: DecisionVerdict;
  label?: string;
}
export const DecisionChip: React.FC<DecisionChipProps> = ({ verdict, label }) => {
  const cls =
    verdict === 'good-deal'
      ? 'cia-chip--deal-good'
      : verdict === 'overpriced'
      ? 'cia-chip--deal-over'
      : 'cia-chip--deal-fair';
  return (
    <span className={`cia-chip ${cls}`}>
      {verdict === 'good-deal' && <CheckIcon />}
      {label ?? decisionToken[verdict].label}
    </span>
  );
};

export interface StatusChipProps {
  status: ListingStatus;
  label?: string;
}
export const StatusChip: React.FC<StatusChipProps> = ({ status, label }) => (
  <span className={`cia-chip cia-chip--status cia-chip--status-${status}`}>
    {label ?? statusToken[status].label}
  </span>
);

export interface LeadChipProps {
  tier: LeadTier;
  label?: string;
}
export const LeadChip: React.FC<LeadChipProps> = ({ tier, label }) => (
  <span className={`cia-chip cia-chip--lead-${tier}`}>{label ?? leadToken[tier].label}</span>
);

export const PremiumBadge: React.FC<{ label?: string }> = ({ label = 'Premium' }) => (
  <span className="cia-badge-premium">
    <SparkleIcon />
    {label}
  </span>
);

/** Appraisal (tasador) reference price chip. */
export const TasadorChip: React.FC<{ price: string }> = ({ price }) => (
  <span className="cia-chip cia-chip--tasador">
    <StarIcon />
    Tasador <strong>{price}</strong>
  </span>
);

/** Price-drop chip: amount + days-on-market. */
export const PriceDropChip: React.FC<{ amount: string; days?: number }> = ({ amount, days }) => (
  <span className="cia-chip cia-chip--pricedrop">
    <ArrowDownIcon />
    Bajó <strong>{amount}</strong>
    {days != null && ` · ${days}d`}
  </span>
);

/** Days-on-market warning chip. */
export const DomChip: React.FC<{ days: number }> = ({ days }) => (
  <span className="cia-chip cia-chip--deal-over">
    <ClockIcon />
    {days}d en venta
  </span>
);
